// Offline Support (120) -- IndexedDB-backed score write-queue (web).
//
// Owns a single `score_write_queue` object store. Score-write helpers in
// api.ts enqueue here when a request fails before any server response (offline
// / timeout); the queue replays FIFO on reconnect. We deliberately own this
// rather than delegate to Workbox Background Sync so we can surface per-item
// status (pending / syncing / rejected) to the coach. See decisions 4, 8, 9.
//
// This module has NO dependency on api.ts: `drainScoreQueue` takes a `replay`
// callback so the queue stays a pure persistence + orchestration unit (and is
// trivially testable with fake-indexeddb).

import { openDB, type IDBPDatabase } from "idb";
import type { OfflineScoreQueueItem } from "@dugads/shared";
import { logger } from "../logger";

const DB_NAME = "dugads-offline";
const DB_VERSION = 1;
const STORE = "score_write_queue";
const IDX_QUEUED_AT = "by_queued_at";

let _dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: "client_event_id" });
          store.createIndex(IDX_QUEUED_AT, "queued_at");
        }
      },
    });
  }
  return _dbPromise;
}

// --- change notification: the UI subscribes to re-read queue state ---

type Listener = () => void;
const listeners = new Set<Listener>();

/** Subscribe to any queue mutation. Returns an unsubscribe. */
export function subscribeQueue(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function notify() {
  for (const fn of listeners) fn();
}

// --- reads ---

/** All queued items, oldest first. */
export async function listScoreQueue(): Promise<OfflineScoreQueueItem[]> {
  const db = await getDb();
  return (await db.getAllFromIndex(STORE, IDX_QUEUED_AT)) as OfflineScoreQueueItem[];
}

/** Queued items for one match, oldest first. */
export async function listQueueForMatch(matchId: string): Promise<OfflineScoreQueueItem[]> {
  return (await listScoreQueue()).filter((i) => i.match_id === matchId);
}

/** Count of items still awaiting the server (pending + in-flight). */
export async function pendingCount(): Promise<number> {
  return (await listScoreQueue()).filter(
    (i) => i.status === "pending" || i.status === "syncing",
  ).length;
}

// --- writes ---

/** Enqueue a score write that failed to reach the server. */
export async function enqueueScoreWrite(item: OfflineScoreQueueItem): Promise<void> {
  const db = await getDb();
  await db.put(STORE, item);
  logger.info(
    { match_id: item.match_id, sport: item.sport, op: item.op },
    "score write queued offline",
  );
  notify();
}

/** Remove a single item (used after the coach re-enters a rejected write). */
export async function removeQueueItem(clientEventId: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, clientEventId);
  notify();
}

/** Drop all rejected items for a match (coach dismissed the rejection notice). */
export async function clearRejectedForMatch(matchId: string): Promise<void> {
  const db = await getDb();
  const rejected = (await listQueueForMatch(matchId)).filter((i) => i.status === "rejected");
  const tx = db.transaction(STORE, "readwrite");
  await Promise.all(rejected.map((i) => tx.store.delete(i.client_event_id)));
  await tx.done;
  if (rejected.length) notify();
}

// --- drain ---

/**
 * Replays one queued item against the server. Resolves on accept; throws on
 * failure. A thrown error carrying a numeric `status` in 4xx range is treated
 * as a hard server rejection; anything else (network / 5xx) is transient.
 */
export type ReplayFn = (item: OfflineScoreQueueItem) => Promise<void>;

export interface DrainResult {
  /** Items the server accepted and we removed from the queue. */
  accepted: number;
  /** Items the server refused (4xx) -- left in queue as `rejected`. */
  rejected: number;
  /** Items still `pending` after the drain stopped (went offline again). */
  remaining: number;
}

let _draining = false;

/**
 * Drain pending items FIFO. Stops at the first transient failure (still
 * offline / server down) -- no point hammering. Hard 4xx rejections are marked
 * `rejected` and the drain continues past them. Re-entrant calls no-op.
 */
export async function drainScoreQueue(replay: ReplayFn): Promise<DrainResult> {
  if (_draining) {
    return { accepted: 0, rejected: 0, remaining: await pendingCount() };
  }
  _draining = true;
  const result: DrainResult = { accepted: 0, rejected: 0, remaining: 0 };
  try {
    const db = await getDb();
    const queue = (await listScoreQueue()).filter((i) => i.status === "pending");
    for (const item of queue) {
      item.status = "syncing";
      item.attempts += 1;
      await db.put(STORE, item);
      notify();
      try {
        await replay(item);
        await db.delete(STORE, item.client_event_id);
        result.accepted += 1;
        notify();
      } catch (err) {
        const status = (err as { status?: number }).status;
        // Hard rejection: the server understood the request and refused it
        // (e.g. 409 match already finalized, 422 invalid). 401/403 are NOT
        // hard -- they mean auth isn't ready / token expired (decision 11,
        // offline auth refresh is out of scope), so treat them as transient
        // and stop draining rather than wrongly rejecting every queued write.
        const isHardReject =
          typeof status === "number" &&
          status >= 400 &&
          status < 500 &&
          status !== 401 &&
          status !== 403;
        if (isHardReject) {
          item.status = "rejected";
          item.rejection = {
            status: status as number,
            message: (err as Error).message || "Rejected by server",
          };
          await db.put(STORE, item);
          result.rejected += 1;
          notify();
          logger.warn(
            { match_id: item.match_id, status, op: item.op },
            "queued score write rejected on replay",
          );
          // Continue: later items may still be valid.
        } else {
          // Transient (network / 5xx / auth-not-ready): revert and stop.
          item.status = "pending";
          await db.put(STORE, item);
          notify();
          logger.info(
            { match_id: item.match_id },
            "queue drain paused -- still offline, server error, or auth not ready",
          );
          break;
        }
      }
    }
    result.remaining = await pendingCount();
    return result;
  } finally {
    _draining = false;
  }
}

/** Test-only: empty the store + reset transient state (keeps the DB open so
 * deleteDatabase can't block on a live connection). */
export async function __clearAllForTests() {
  const db = await getDb();
  await db.clear(STORE);
  listeners.clear();
  _draining = false;
}
